/*
  Make an Offer → SalesChannelHub.

  The offer form (snippets/product-offer-form.liquid) posts to the shop's
  SalesChannelHub offers inbox instead of the store contact form. When the
  dialog opens we fetch a one-time token from the widget config endpoint; on
  submit we post the form there and show the result inside the dialog, using
  the same status markup and .is-sent state the contact dialogs use (so
  product.js's close handler resets it).

  Field validity (required, email, the amount pattern) is the browser's own;
  this adds the shop's minimum-offer rule and the photo limits.
*/
(() => {
  if (window.psjOffer) return;
  window.psjOffer = true;

  const TOKEN_TTL = 25 * 60 * 1000; // the token lives 30 minutes server-side
  const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const state = new WeakMap(); // form → { config, fetchedAt, loading }

  const apiBase = (form) => (form.dataset.api || 'https://saleschannelhub.com').replace(/\/+$/, '');

  const clearStatus = (form) => {
    form.querySelectorAll('[data-contact-status]').forEach((el) => el.remove());
  };

  const showStatus = (form, kind, message) => {
    clearStatus(form);
    const el = document.createElement(kind === 'success' ? 'p' : 'div');
    el.className = kind === 'success' ? 'form-status' : 'form-status form-status--error';
    el.setAttribute('role', kind === 'success' ? 'status' : 'alert');
    el.tabIndex = -1;
    el.dataset.contactStatus = kind;
    el.textContent = message;
    form.prepend(el);
    el.focus();
    return el;
  };

  const setBusy = (form, busy) => {
    const button = form.querySelector('[type="submit"]');
    if (!button) return;
    if (busy) {
      button.dataset.label = button.dataset.label || button.textContent;
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
      button.textContent = form.dataset.msgSending || button.dataset.sendingText || button.dataset.label;
    } else {
      button.removeAttribute('aria-disabled');
      button.removeAttribute('aria-busy');
      if (button.dataset.label) button.textContent = button.dataset.label;
    }
  };

  const parseAmount = (value) => parseFloat(String(value || '').replace(/[$,\s]/g, ''));

  const loadConfig = async (form, force = false) => {
    const current = state.get(form) || {};
    if (!force && current.config && Date.now() - current.fetchedAt < TOKEN_TTL) return current.config;
    if (current.loading) return current.loading;

    const url = `${apiBase(form)}/api/widget/offer/config?key=${encodeURIComponent(form.dataset.key || '')}`;
    const loading = fetch(url, { headers: { Accept: 'application/json' }, credentials: 'omit' })
      .then((response) => response.json().catch(() => ({ enabled: false })))
      .then((config) => {
        state.set(form, { config, fetchedAt: Date.now(), loading: null });
        return config;
      })
      .catch((error) => {
        state.set(form, { config: null, fetchedAt: 0, loading: null });
        throw error;
      });
    state.set(form, { ...current, loading });
    return loading;
  };

  const forgetToken = (form) => state.set(form, { config: null, fetchedAt: 0, loading: null });

  const applyPaused = (form, config) => {
    const button = form.querySelector('[type="submit"]');
    if (config && config.enabled === false) {
      showStatus(form, 'error', form.dataset.msgPaused);
      if (button) button.setAttribute('aria-disabled', 'true');
    }
  };

  // Fetch the token as the dialog opens, so sending is instant.
  const watchDialog = (form) => {
    const dialog = form.closest('dialog');
    if (!dialog || dialog.dataset.offerWatched) return;
    dialog.dataset.offerWatched = 'true';
    new MutationObserver(() => {
      if (!dialog.open) return;
      loadConfig(form)
        .then((config) => applyPaused(form, config))
        .catch(() => {}); // tried again on submit
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });
  };

  const localError = (form) => {
    const amount = form.querySelector('[data-offer-amount]');
    const offer = parseAmount(amount && amount.value);
    if (!(offer > 0)) return amount && amount.dataset.invalidMessage;

    const photo = form.querySelector('[data-offer-photo]');
    const file = photo && photo.files && photo.files[0];
    if (file) {
      const config = (state.get(form) || {}).config || {};
      const maxKb = config.photo_max_kb || 5120;
      if (file.size > maxKb * 1024) return form.dataset.msgPhotoSize;
      if (file.type && !PHOTO_TYPES.includes(file.type)) return form.dataset.msgPhotoType;
    }
    return null;
  };

  const belowMinimum = (form, config) => {
    const min = config && config.min_offer_percent;
    const list = parseFloat((form.querySelector('[data-asking-raw-input]') || {}).value);
    const offer = parseAmount((form.querySelector('[data-offer-amount]') || {}).value);
    return Boolean(min && list > 0 && (offer / list) * 100 < min);
  };

  const send = async (form, config) => {
    const data = new FormData(form);
    const amount = parseAmount(data.get('amount'));
    data.delete('amount');
    data.set('offered_price', amount.toFixed(2));
    data.set('key', form.dataset.key || '');
    data.set('nonce', config.nonce || '');
    data.set('nonce_signature', config.nonce_signature || '');
    // The server's own clock from the config call — a shopper's device clock may be off.
    data.set('rendered_at', String(config.issued_at || Math.floor(Date.now() / 1000) - 5));
    const photo = data.get('photo');
    if (photo instanceof File && photo.size === 0) data.delete('photo');

    const response = await fetch(`${apiBase(form)}/api/widget/offers`, {
      method: 'POST',
      body: data,
      headers: { Accept: 'application/json' },
      credentials: 'omit',
    });
    const body = await response.json().catch(() => ({}));
    return { status: response.status, body };
  };

  const errorMessage = (form, body) => {
    if (body && body.message) return body.message;
    const errors = body && body.errors;
    if (errors && typeof errors === 'object') {
      const first = Object.values(errors)[0];
      if (Array.isArray(first) && first[0]) return first[0];
    }
    return form.dataset.msgError;
  };

  const onSubmit = async (event) => {
    const form = event.target.closest && event.target.closest('form[data-offer-form]');
    if (!form) return;
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    if (button && button.getAttribute('aria-busy') === 'true') return;

    const problem = localError(form);
    if (problem) {
      showStatus(form, 'error', problem);
      return;
    }

    setBusy(form, true);
    try {
      let config = await loadConfig(form);
      if (!config || config.enabled === false) {
        showStatus(form, 'error', config ? form.dataset.msgPaused : form.dataset.msgError);
        return;
      }
      if (belowMinimum(form, config)) {
        showStatus(form, 'error', form.dataset.msgMin);
        return;
      }

      let result = await send(form, config);
      // An expired token: fetch a fresh one and try once more.
      if (result.status === 422 && result.body && /expired|timed out/i.test(result.body.message || '')) {
        config = await loadConfig(form, true);
        result = await send(form, config);
      }

      if (result.body && result.body.ok) {
        forgetToken(form); // single use
        const reference = result.body.reference;
        const message = reference
          ? (form.dataset.msgSuccess || '').replace('%REF%', reference)
          : form.dataset.msgSuccessPlain;
        const dialog = form.closest('dialog');
        showStatus(form, 'success', message);
        if (dialog) dialog.classList.add('is-sent');
        return;
      }

      showStatus(form, 'error', errorMessage(form, result.body));
    } catch (error) {
      showStatus(form, 'error', form.dataset.msgError);
    } finally {
      setBusy(form, false);
    }
  };

  const init = () => {
    document.querySelectorAll('form[data-offer-form]').forEach(watchDialog);
  };

  document.addEventListener('submit', onSubmit);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
